<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { ChevronDown } from 'lucide-vue-next'
import { useI18n } from '@/composables/useI18n'

// NOTE: deliberately does NOT extend BaseComponentProps. Declaring `class` /
// `style` / `id` as props would swallow them (Vue stops treating them as
// fallthrough attrs), so `<Dropdown class="hidden md:block">` rendered nothing
// on the root and desktop-only dropdowns showed up on mobile. Leaving them
// undeclared lets Vue merge them onto the root element as usual.
interface Props {
  modelValue?: boolean
  trigger?: 'click' | 'hover'
  placement?: 'bottom' | 'top' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  closeOnOutsideClick?: boolean
  closeOnEscape?: boolean
  teleport?: boolean | string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  trigger: 'click',
  placement: 'bottom',
  align: 'start',
  closeOnOutsideClick: true,
  closeOnEscape: true,
  teleport: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
  open: []
}>()

const { t } = useI18n()

const isOpen = computed({
  get: () => props.modelValue,
  set: (val: boolean) => {
    emit('update:modelValue', val)
    if (val) emit('open')
    else emit('close')
  }
})

const dropdownRef = ref<HTMLDivElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const contentRef = ref<HTMLDivElement | null>(null)
const isHovering = ref(false)
const previouslyFocused = ref<HTMLElement | null>(null)

// One id per instance: several dropdowns share a page (region, language,
// categories) and used to share id="dropdown-content".
const contentId = `dropdown-${Math.random().toString(36).slice(2, 9)}`

// The ARIA state belongs on the control people actually focus — the button
// inside the trigger slot — not on the wrapping <div>, where screen readers
// ignore it (a div has no role that allows aria-expanded).
function syncTriggerAria() {
  const control = triggerRef.value?.querySelector<HTMLElement>('button, [href], [role="button"], [tabindex]:not([tabindex="-1"])')
  if (!control) return
  control.setAttribute('aria-haspopup', 'true')
  control.setAttribute('aria-expanded', String(isOpen.value))
  if (isOpen.value) control.setAttribute('aria-controls', contentId)
  else control.removeAttribute('aria-controls')
}

const placementClasses = computed(() => {
  switch (props.placement) {
    case 'top':
      return 'bottom-full mb-2'
    case 'left':
      return 'right-full mr-2 top-0'
    case 'right':
      return 'left-full ml-2 top-0'
    case 'bottom':
    default:
      return 'top-full mt-2'
  }
})

const alignClasses = computed(() => {
  switch (props.align) {
    case 'end':
      return 'right-0'
    case 'center':
      return 'left-1/2 -translate-x-1/2'
    case 'start':
    default:
      return 'left-0'
  }
})

function open() {
  isOpen.value = true
}

function close() {
  isOpen.value = false
}

function toggle() {
  isOpen.value = !isOpen.value
}

function handleTriggerClick(event: MouseEvent) {
  event.stopPropagation()
  // In hover mode the pointer has ALREADY opened the panel through mouseenter by
  // the time the click lands, so toggling here would slam it shut again and mouse
  // users would only ever see a flash (this broke the desktop "Все категории"
  // mega-menu). Keyboard activation still has to work: Tab + Enter/Space fires a
  // native click with no pointer behind it (detail === 0, and the trigger is not
  // hovered), and only that case may toggle.
  if (props.trigger === 'hover') {
    const fromPointer = isHovering.value || event.detail > 0
    if (fromPointer) return
  }
  toggle()
}

let hoverTimer: ReturnType<typeof setTimeout> | null = null

function handleTriggerMouseEnter() {
  if (props.trigger === 'hover') {
    if (hoverTimer) clearTimeout(hoverTimer)
    isHovering.value = true
    open()
  }
}

function handleTriggerMouseLeave() {
  if (props.trigger === 'hover') {
    isHovering.value = false
    hoverTimer = setTimeout(() => {
      if (!isHovering.value) close()
    }, 150)
  }
}

function handleContentMouseEnter() {
  if (props.trigger === 'hover') {
    if (hoverTimer) clearTimeout(hoverTimer)
    isHovering.value = true
  }
}

function handleContentMouseLeave() {
  if (props.trigger === 'hover') {
    isHovering.value = false
    hoverTimer = setTimeout(() => {
      if (!isHovering.value) close()
    }, 150)
  }
}

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.closeOnEscape) {
    close()
    triggerRef.value?.focus()
    return
  }
  
  if (event.key === 'Tab' && contentRef.value) {
    const focusableElements = contentRef.value.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusableElements.length === 0) return
    
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]
    
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }
}

function handleOutsideClick(event: MouseEvent) {
  if (!props.closeOnOutsideClick || !isOpen.value) return
  
  const target = event.target as Node | null
  if (!target) return
  
  const clickedTrigger = triggerRef.value?.contains(target) ?? false
  const clickedContent = contentRef.value?.contains(target) ?? false
  const clickedDropdown = dropdownRef.value?.contains(target) ?? false
  
  if (!clickedTrigger && !clickedContent && !clickedDropdown) {
    close()
  }
}

watch(isOpen, async (val) => {
  if (val) {
    previouslyFocused.value = (document.activeElement as HTMLElement) || null
    document.addEventListener('click', handleOutsideClick)
    document.addEventListener('keydown', handleKeyDown)
    
    await nextTick()
    const focusable = contentRef.value?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    focusable?.focus()
  } else {
    document.removeEventListener('click', handleOutsideClick)
    document.removeEventListener('keydown', handleKeyDown)
  }
})

watch(isOpen, () => nextTick(syncTriggerAria))

onMounted(() => {
  syncTriggerAria()
  if (isOpen.value) {
    document.addEventListener('click', handleOutsideClick)
    document.addEventListener('keydown', handleKeyDown)
  }
})

onUnmounted(() => {
  if (hoverTimer) clearTimeout(hoverTimer)
  document.removeEventListener('click', handleOutsideClick)
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <div
    ref="dropdownRef"
    class="relative inline-block text-left"
  >
    <!-- Trigger Slot -->
    <div
      ref="triggerRef"
      class="cursor-pointer inline-flex items-center"
      @click="handleTriggerClick"
      @mouseenter="handleTriggerMouseEnter"
      @mouseleave="handleTriggerMouseLeave"
    >
      <slot name="trigger">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-xl border border-border bg-surface px-3.5 py-2.5 text-sm font-semibold text-text-primary transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span>{{ t('common.menu') }}</span>
          <ChevronDown
            :class="['w-3.5 h-3.5 transition-transform duration-200', { 'rotate-180': isOpen }]"
            aria-hidden="true"
          />
        </button>
      </slot>
    </div>
    
    <!-- Content - Teleported if teleport prop specified -->
    <Teleport v-if="isOpen && props.teleport" :to="props.teleport === true ? 'body' : props.teleport">
      <Transition name="dropdown">
        <div
          :id="contentId"
          ref="contentRef"
          :class="['dropdown-menu absolute z-50', placementClasses, alignClasses]"
          role="menu"
          @mouseenter="handleContentMouseEnter"
          @mouseleave="handleContentMouseLeave"
        >
          <slot />
        </div>
      </Transition>
    </Teleport>
    
    <!-- Non-teleported fallback (Default, perfect for inline components) -->
    <Transition v-else-if="isOpen" name="dropdown">
      <div
        :id="contentId"
        ref="contentRef"
        :class="['dropdown-menu absolute z-50', placementClasses, alignClasses]"
        role="menu"
        @mouseenter="handleContentMouseEnter"
        @mouseleave="handleContentMouseLeave"
      >
        <slot />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.15s ease-out;
}

.dropdown-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
}

/* Focus visible styles for accessibility — Kyrgyz Gold token, not a raw hex */
.dropdown-menu :focus-visible {
  outline: 2px solid rgb(var(--color-primary));
  outline-offset: 2px;
}

/* No surface styling on .dropdown-menu itself: it is only a positioning shell.
   Every consumer supplies its own `rounded-2xl border border-border bg-surface`
   panel inside the slot, so painting a second surface here drew a duplicate
   (and off-palette) ring around it. */
</style>